import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { DailyUpdateCard } from '@terminal/components/daily-update-card/DailyUpdateCard';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { usePulseDailyUpdate } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import styles from './RecentUpdates.module.scss';

export interface RecentUpdatesProps {
  themeId: string;
}

export const RecentUpdates = ({ themeId }: RecentUpdatesProps) => {
  const { t } = useTranslation(['widget']);
  const dailyUpdate = usePulseDailyUpdate({ id: themeId });

  const createdAt = dailyUpdate.data?.createdAt;
  const lastUpdated = createdAt
    ? t('widget:themeOverview.pulse.lastUpdatedAt', {
        date: format(new Date(createdAt), 'h:mm a, MMM d'),
      })
    : undefined;

  if (!dailyUpdate.isLoading && dailyUpdate.data === undefined) {
    return null;
  }

  return (
    <section
      className={styles.root}
      data-testid="industry-drawer-recent-updates"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('widget:themeOverview.pulse.recentUpdates')}
        </h3>
        {lastUpdated && <span className={styles.meta}>{lastUpdated}</span>}
      </div>
      {dailyUpdate.isLoading || !dailyUpdate.data ? (
        <SkeletonLoader
          areas={[
            {
              styles: { width: '100%', height: '60px', borderRadius: '8px' },
            },
          ]}
        />
      ) : (
        <ErrorBoundaryWrapper showBorder={false}>
          <DailyUpdateCard
            update={dailyUpdate.data.content}
            themeId={themeId}
            hasCitationLinks={false}
          />
        </ErrorBoundaryWrapper>
      )}
    </section>
  );
};
